import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import api, { fmtDateTimeParis } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Bot, Link2, CheckCircle2, AlertTriangle, ShieldCheck, Flame, Coins, Wallet, RefreshCw, EyeOff, PenLine, MessageSquareShare, BookOpen, Ban, ListChecks, Copy, Users, HelpCircle, ChevronDown, Repeat, Eye } from 'lucide-react';

// Page « Bot d'accueil » — bot du CRM n°2 (externe) qui accueille les NOUVEAUX fans
// jusqu'au premier lien de paiement, puis rend la main aux chatteurs.
// E1 (Marlon 14/09) : le bot tourne dans le CRM n°2, ChatHub le SUIT. Enregistrer la modèle et le lien = ChatHub actif
// tout de suite ; la page dit simplement ce que fait ChatHub (aucun bouton lancer / pause). E2 : modèle et lien modifiables
// par Owner / Co-owner (manage_telegram, `peut_modifier`) ; les managers voient et utilisent tout le reste de la page.
// La seconde moitié de la page est un AIDE-MÉMOIRE demandé par Marlon : le fonctionnement
// décidé, tel qu'il se présentera. Source : 3D/FONCTIONNEMENT_BOT_PASSATION.md.

const ETAPES = [
  { icon: EyeOff, titre: 'Le nouveau fan entre dans la Zone Bot', texte: "Caché aux chatteurs tant que le bot lui parle. Le manager le voit." },
  { icon: Bot, titre: 'Le bot discute et fait la découverte', texte: 'Prénom, âge, ce qui lui plaît : le chatteur ne reposera pas ces questions.' },
  { icon: Link2, titre: 'Le bot envoie son lien de paiement', texte: "Toujours le même lien — c'est le déclencheur." },
  { icon: PenLine, titre: 'ChatHub remplace le lien par un lien au nom du fan', texte: 'Même page, même prix, même contenu. Le fan voit juste « modifié ». Testé le 12/09 : 2 à 3 secondes.' },
  { icon: MessageSquareShare, titre: 'Le fan revient aux chatteurs', texte: "Après le lien du bot et sa réaction (🟢 prêt à payer, 🟡 à négocier) : chatteur en shift, réparti par le planning. 🔴 time waster : reste caché. Sans lien au bout de 3 jours : rendu." },
  { icon: Coins, titre: 'Quand il paie, ChatHub sait qui', texte: 'La première vente est créditée « Bot ». Toutes les suivantes, au chatteur.' },
];

const NOTIFS = [
  { couleur: 'orange', icon: Flame, titre: 'Le bot te passe la main', texte: 'Jacques · Sara — → Marie · Box 2', quand: 'Le bot vient d’envoyer son lien → le chatteur qui reprend négocie. Pour lui et les managers. Reste affichée jusqu’à ce qu’on la ferme.' },
  { couleur: 'violet', icon: Bot, titre: 'Vente du bot — 20,00 €', texte: 'Jacques · Sara — il a payé, continue la conversation', quand: 'Le fan a payé le lien du bot. Pour le chatteur qui l’a repris et les managers. Couleur à part pour ne jamais la confondre avec une vente d’équipe.' },
  { couleur: 'vert', icon: Wallet, titre: 'Vente DropFans — 42,00 €', texte: 'Jacques · Marie', quand: 'Un chatteur a vendu. Notification d’aujourd’hui, inchangée.' },
];

const TEINTE = {
  orange: 'border-orange-500/30 bg-orange-500/[0.07] text-orange-300',
  violet: 'border-violet-500/30 bg-violet-500/[0.08] text-violet-300',
  vert: 'border-emerald-500/30 bg-emerald-500/[0.07] text-emerald-300',
};

// Exemple d'affichage de la liste des ventes après le lancement (données fictives).
const VENTES_EXEMPLE = [
  { heure: '21:42', fan: 'Jacques', par: 'bot', montant: '20,00 €', note: 'première vente, faite par le bot' },
  { heure: '22:15', fan: 'Jacques', par: 'Marie', montant: '42,00 €', note: 'Marie a repris Jacques : cette vente est à elle' },
  { heure: '22:31', fan: 'Thomas', par: 'Tefy', montant: '35,00 €', note: 'un fan habituel, rien ne change' },
];

const A_FAIRE = [
  'Cliquer « Ouvrir le chat » dès la notification orange',
  'Lire les notes du fan avant le premier message',
  'Enchaîner directement sur les photos gratuites, puis le script',
  'Accompagner et négocier jusqu’au paiement s’il n’a pas encore payé',
];

const A_NE_PAS_FAIRE = [
  'Redemander le prénom, l’âge ou le métier — le bot l’a déjà fait',
  'Se présenter : c’est la suite d’une conversation, pas une nouvelle',
  'Renvoyer le lien du bot — c’est grâce à lui qu’on sait quand le fan paie',
];

const messageChatteurs = (modele) => `🤖 Nouveau fonctionnement à partir du [DATE] — les nouveaux fans de ${modele}

À partir du [DATE], un bot s'occupe des nouveaux fans de ${modele} : il leur répond, fait connaissance avec eux, et leur envoie le lien de paiement. Vous ne verrez pas ces conversations pendant ce temps, c'est normal.

Dès que le bot a envoyé son lien et que le fan y a réagi, il vous revient (sauf s'il refuse clairement de payer). Sans lien du bot au bout de 3 jours, il revient aussi.
Arrivé hors de ton shift : il t'attend dans ta box, tu es prévenu au début de ton shift.

🟠 Notification orange — « Le bot te passe la main »
Le bot vient d'envoyer le lien. C'est à vous. Le fan arrive directement dans ta box.
1. Clique sur « Ouvrir le chat »
2. Lis les notes du fan avant d'écrire quoi que ce soit : tout ce que le bot a appris est dedans
3. Enchaîne directement sur les photos gratuites et continue le script

⛔ Ne lui redemande pas son prénom, son âge ou ce qu'il fait : le bot l'a déjà fait, il a déjà répondu.
⛔ Ne te présente pas : c'est la suite d'une conversation, pas une nouvelle.
⛔ Ne renvoie jamais le lien du bot. Le fan l'a déjà, et c'est grâce à lui qu'on sait quand il paie.

Si le fan n'a pas encore payé : accompagne-le, rassure-le, négocie jusqu'au paiement.

🟣 Notification violette — « Vente du bot »
Le fan a payé le lien du bot. Ouvre le chat, remercie-le, et continue le script comme d'habitude.

🟢 Notification verte — « Vente DropFans »
Rien ne change : ce sont vos ventes, comme aujourd'hui.

💰 Vos ventes
La première vente, celle du bot, ne compte pas dans vos chiffres. Toutes les ventes que vous faites ensuite avec ce fan sont à vous, normalement.

Cas rare : si un fan te dit qu'il a payé mais que rien n'apparaît sur sa conversation, préviens le manager : il le rattache en un clic. Ça ne change rien à tes chiffres.

Ce qui ne change pas : tous vos fans actuels, vos baleines, vos relances, vos scripts. Le bot ne parle qu'aux nouveaux.`;

const messageManager = (modele) => `Bot d'accueil sur ${modele} — [DATE]

LE PRINCIPE
Le bot du CRM n°2 prend en charge les nouveaux fans de ${modele}, du premier message jusqu'à l'envoi du lien de paiement. Dès que le lien est parti, le bot classe le fan : 🟢 prêt à payer et 🟡 à négocier reviennent aux chatteurs, 🔴 time waster reste caché (visible dans « Lien envoyé »). Sans lien au bout de 3 jours, le fan revient aussi.

CE QUI EST AUTOMATIQUE — tu n'as rien à faire
- Le lien envoyé par le bot est remplacé en 2-3 secondes par un lien au nom du fan. Même page, même prix, même contenu pour lui. Résultat : quand il paie, ChatHub sait qui c'est. Plus de vente à rattacher à la main.
- Le fan va au prochain chatteur en shift, dans sa box : il reçoit une notification orange quand le bot passe la main, et une violette quand le fan a payé. Tu reçois aussi chaque notification.
- Personne en shift : le fan va tout de suite dans la box du chatteur du prochain shift. Tu es prévenu tout de suite, lui au début de son shift.
- La première vente est créditée au Bot, jamais à un chatteur. Toutes les suivantes vont au chatteur qui a repris le fan.

CE QUE TU SURVEILLES
- Que les reprises se font vite. Un fan qui reçoit le lien et que personne ne reprend, c'est un fan chaud perdu. Si une notification orange traîne, relance le chatteur.
- Que personne n'envoie le lien du bot. Il est réservé au bot. S'il part chez un chatteur, ses ventes seraient créditées au bot. ChatHub te prévient dans la page « Bot d'accueil » si ça arrive.
- Le cas rare « C'est lequel ? » : un fan qui a cliqué le lien d'origine dans les 2-3 secondes avant son remplacement. Tu choisis le fan dans la page Bot d'accueil (« Ventes du bot à identifier »).

OÙ VOIR CE QUI SE PASSE
Menu → Bot d'accueil : la modèle et le lien du bot, les fans à qui il a envoyé son lien, et les ventes faites par le bot. Dans la liste des ventes, celles du bot ont un badge violet « Bot ».

CE QUI NE CHANGE PAS
Les fans existants, les baleines, les relances, les scripts, les stats des chatteurs. Le bot ne parle qu'aux nouveaux fans, et jamais à un fan déjà suivi.`;

function Carte({ titre, icon: Icon, children, droite, testid }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 mb-4" data-testid={testid}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="text-[15px] font-semibold text-white flex items-center gap-2">{Icon && <Icon size={17} className="text-slate-400" />}{titre}</h2>
        {droite}
      </div>
      {children}
    </div>
  );
}

function MessageACopier({ titre, pour, texte, testid }) {
  const [copie, setCopie] = useState(false);
  // Le presse-papiers moderne est refusé dans certains contextes (onglet sans focus,
  // fenêtre intégrée, permission bloquée) : on retombe alors sur l'ancienne méthode,
  // qui passe presque partout. Constaté le 12/09 dans l'aperçu local.
  const copierSecours = () => {
    const zone = document.createElement('textarea');
    zone.value = texte;
    zone.setAttribute('readonly', '');
    zone.style.position = 'fixed';
    zone.style.opacity = '0';
    document.body.appendChild(zone);
    zone.select();
    let ok = false;
    try { ok = document.execCommand('copy'); } catch { ok = false; }
    document.body.removeChild(zone);
    return ok;
  };
  const copier = async () => {
    let ok = false;
    try {
      await navigator.clipboard.writeText(texte);
      ok = true;
    } catch {
      ok = copierSecours();
    }
    if (ok) {
      setCopie(true);
      toast.success('Message copié');
      setTimeout(() => setCopie(false), 1800);
    } else {
      toast.error('Copie impossible — ouvre le message et sélectionne le texte à la main.');
    }
  };
  return (
    <details className="group rounded-xl border border-white/10 bg-black/20" data-testid={testid}>
      <summary className="cursor-pointer list-none px-4 py-3 flex items-center justify-between gap-3">
        <span className="flex items-center gap-2 text-[13px] font-semibold text-white">
          <ChevronDown size={15} className="text-slate-500 transition-transform group-open:rotate-180" />
          {titre}
          <span className="text-[11px] font-normal text-slate-500">· {pour}</span>
        </span>
        <button type="button" onClick={(e) => { e.preventDefault(); copier(); }}
          className="text-[12px] px-2.5 py-1 rounded-md border border-white/10 text-slate-300 hover:text-white hover:bg-white/5 flex items-center gap-1.5">
          {copie ? <CheckCircle2 size={13} className="text-emerald-400" /> : <Copy size={13} />}
          {copie ? 'Copié' : 'Copier'}
        </button>
      </summary>
      <pre className="px-4 pb-4 whitespace-pre-wrap font-sans text-[12.5px] leading-relaxed text-slate-300">{texte}</pre>
    </details>
  );
}

// Le backend renvoie des dates UTC sans fuseau : on passe par le helper commun, qui
// ajoute le Z manquant et affiche en heure de Paris (sinon décalage de 2 h).
function dateFr(v) {
  if (!v) return '—';
  return fmtDateTimeParis(v) || '—';
}

export default function CrmBot() {
  const [etat, setEtat] = useState(null);
  const [apercu, setApercu] = useState(null);
  const [comptes, setComptes] = useState([]);
  const [compte, setCompte] = useState('');
  const [lien, setLien] = useState('');
  const [enreg, setEnreg] = useState(false);
  const [chargeApercu, setChargeApercu] = useState(false);
  const [configChargee, setConfigChargee] = useState(false);
  const [erreurConfig, setErreurConfig] = useState(false);
  const [erreurApercu, setErreurApercu] = useState(false);
  // Reprise des fans : box facultative de chaque chatteur (V5 : plus de fiche du fan à la reprise).
  const [reprise, setReprise] = useState(null);
  const [repriseKo, setRepriseKo] = useState(false);
  const [boxes, setBoxes] = useState({});
  const [enregReprise, setEnregReprise] = useState(false);
  const [erreurReprise, setErreurReprise] = useState('');
  // Ventes du bot arrivées sans nom (lien d'origine cliqué avant son remplacement).
  const [aIdentifier, setAIdentifier] = useState({ ventes: [], candidats: [] });
  const [choixFan, setChoixFan] = useState({});
  const [identifEnCours, setIdentifEnCours] = useState('');
  const [rechercheFan, setRechercheFan] = useState({});
  // V6 : carte « Time wasters » (managers). null = pas chargée ou pas le droit (carte masquée).
  const [timeWasters, setTimeWasters] = useState(null);
  const [twEnCours, setTwEnCours] = useState('');
  const [twFil, setTwFil] = useState({ id: '', messages: [], charge: false, erreur: '' });
  const minuterieDemo = useRef(null);
  const navigate = useNavigate();
  const { hasPerm } = useAuth();

  const chargerApercu = useCallback(() => {
    setChargeApercu(true);
    api.get('/crm-bot/apercu')
      .then(({ data }) => { setApercu(data); setErreurApercu(false); })
      .catch(() => setErreurApercu(true))   // on garde l'aperçu précédent au lieu de l'effacer
      .finally(() => setChargeApercu(false));
  }, []);

  // Les listes déroulantes partent de ce qui est ENREGISTRÉ (sert aussi à savoir si quelque chose a changé).
  const appliquerReprise = useCallback((data) => {
    setReprise(data);
    const b = {};
    (data?.chatteurs || []).forEach((c) => { b[c.id] = c.box || null; });
    setBoxes(b);
  }, []);

  const chargerReprise = useCallback(() => {
    api.get('/crm-bot/reprise')
      .then(({ data }) => { appliquerReprise(data); setRepriseKo(false); })
      .catch(() => setRepriseKo(true));
  }, [appliquerReprise]);

  // Relit l'état (alerte, pret…) sans toucher aux champs du formulaire.
  const relireEtat = useCallback(() => {
    api.get('/crm-bot/config').then(({ data }) => setEtat(data)).catch(() => {});
  }, []);

  const chargerAIdentifier = useCallback(() => {
    api.get('/crm-bot/ventes-a-identifier')
      .then(({ data }) => setAIdentifier({
        ventes: Array.isArray(data?.ventes) ? data.ventes : [],
        candidats: Array.isArray(data?.candidats) ? data.candidats : [],
      }))
      .catch(() => {});   // carte simplement masquée si la liste ne charge pas
  }, []);

  const chargerTimeWasters = useCallback(() => {
    api.get('/zone-bot/time-wasters')
      .then(({ data }) => setTimeWasters(Array.isArray(data?.fans) ? data.fans : []))
      .catch(() => setTimeWasters(null));
  }, []);

  useEffect(() => {
    api.get('/accounts').then(({ data }) => setComptes(Array.isArray(data) ? data : [])).catch(() => {});
    api.get('/crm-bot/config').then(({ data }) => {
      setEtat(data);
      setCompte(data?.account_id || '');
      setLien(data?.link_url || '');
    }).catch(() => setErreurConfig(true)).finally(() => setConfigChargee(true));
    chargerApercu();
    chargerReprise();
    chargerAIdentifier();
    chargerTimeWasters();
  }, [chargerApercu, chargerReprise, chargerAIdentifier, chargerTimeWasters]);

  // Démo des notifications : on n'affiche pas la violette si on a quitté la page entre-temps.
  useEffect(() => {
    const minuterie = minuterieDemo;
    return () => clearTimeout(minuterie.current);
  }, []);

  const enregistrer = async () => {
    if (!compte) { toast.error('Choisis la modèle sur laquelle tourne le bot.'); return; }
    if (!lien.trim()) { toast.error('Colle le lien Dropp que le bot envoie.'); return; }
    setEnreg(true);
    try {
      const { data } = await api.put('/crm-bot/config', { account_id: compte, link_url: lien.trim() });
      setEtat(data);
      setCompte(data?.account_id || compte);
      setLien(data?.link_url || lien.trim());
      // E1 : enregistré = ChatHub suit le bot tout de suite ; le toast dit ce que le serveur a écrit.
      toast.success(/^actif/i.test(String(data?.statut || '')) ? `Enregistré : ChatHub suit le bot sur ${data.account_name || 'cette modèle'}.` : 'Réglages enregistrés.');
      chargerApercu();
      chargerReprise();   // la modèle a pu changer : autres chatteurs, autres box, fans de l'ancienne rendus
      chargerAIdentifier();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Enregistrement impossible.');
    } finally {
      setEnreg(false);
    }
  };

  const modifieReprise = !!reprise?.configure && (
    (reprise.chatteurs || []).some((c) => (boxes[c.id] || null) !== (c.box || null))
  );

  const enregistrerReprise = async () => {
    setEnregReprise(true);
    setErreurReprise('');
    try {
      const { data } = await api.put('/crm-bot/reprise', { boxes_chatteurs: boxes });
      appliquerReprise(data);
      toast.success('Réglage enregistré.');
      relireEtat();   // alertes du serveur relues tout de suite
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErreurReprise(typeof d === 'string' ? d : 'Enregistrement impossible.');
    } finally {
      setEnregReprise(false);
    }
  };

  // V6 : un time waster rendu aux chatteurs sort de la Zone Bot (même route que la messagerie) et réapparaît dans la liste.
  const rendreTimeWaster = async (f) => {
    setTwEnCours(f.conversation_id);
    try {
      const { data } = await api.post(`/zone-bot/${f.conversation_id}/rendre`);
      const r = data?.reprise || {};
      toast.success(r.bloque ? `${f.nom} sort de la Zone Bot (toujours bloqué)` : r.chatter_name ? `${f.nom} rendu à ${r.chatter_name}` : `${f.nom} rendu aux chatteurs`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Impossible de rendre ce fan.');
    } finally {
      setTwEnCours('');
      if (twFil.id === f.conversation_id) setTwFil({ id: '', messages: [], charge: false, erreur: '' });
      chargerTimeWasters();
      chargerReprise();
    }
  };

  // V6 : « Ouvrir la conversation » d'un time waster → fil en lecture seule, sur place (il n'est dans aucune liste).
  const ouvrirTimeWaster = async (f) => {
    const id = f.conversation_id;
    if (twFil.id === id) { setTwFil({ id: '', messages: [], charge: false, erreur: '' }); return; }
    setTwFil({ id, messages: [], charge: true, erreur: '' });
    try {
      const { data } = await api.get(`/conversations/${id}/messages`, { params: { peek: true } });
      setTwFil((o) => (o.id === id ? { ...o, messages: Array.isArray(data) ? data : [], charge: false } : o));
    } catch (e) {
      setTwFil((o) => (o.id === id ? { ...o, charge: false, erreur: 'Conversation impossible à charger.' } : o));
    }
  };

  // « C'est lui » : rattache la vente au fan. Elle reste créditée au Bot.
  const identifierVente = async (v) => {
    const cid = choixFan[v.id];
    if (!cid) return;
    setIdentifEnCours(v.id);
    try {
      await api.post(`/crm-bot/ventes/${v.id}/fan`, { conversation_id: cid });
      toast.success('Fan identifié.');
      chargerAIdentifier();
      chargerApercu();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Identification impossible.');
      chargerAIdentifier();
    } finally {
      setIdentifEnCours('');
    }
  };

  // « Voir en vrai » : mêmes toasts que DashboardLayout (bot_main puis bot_vente), exemple fictif.
  const voirNotifs = () => {
    const ouvrir = { label: 'Ouvrir le chat', onClick: () => {} };   // exemple : pas de vrai chat derrière
    toast(
      <div className="flex flex-col gap-0.5" data-testid="crm-bot-demo-main">
        <div className="font-bold text-orange-300 flex items-center gap-1.5">🔥 Le bot te passe la main</div>
        <div className="text-[11px] text-slate-300">Jacques · Sara — → <b className="text-white">Marie</b> · Box 2</div>
      </div>,
      { id: 'demo-bot-main', duration: Infinity, closeButton: true, action: ouvrir }
    );
    clearTimeout(minuterieDemo.current);
    minuterieDemo.current = setTimeout(() => {
      toast(
        <div className="flex flex-col gap-0.5" data-testid="crm-bot-demo-vente">
          <div className="font-bold text-violet-300 flex items-center gap-1.5">🤖 Vente du bot — 20,00 €</div>
          <div className="text-[11px] text-slate-300">Jacques · Sara — il a payé, continue la conversation</div>
        </div>,
        { id: 'demo-bot-vente', duration: Infinity, closeButton: true, action: ouvrir }
      );
    }, 1500);
  };

  // Un même fan peut avoir reçu le lien plusieurs fois : on ne le propose qu'une fois (le plus récent).
  const candidats = aIdentifier.candidats.filter((c, i, t) => c.conversation_id && t.findIndex((x) => x.conversation_id === c.conversation_id) === i);
  // Recherche par nom (sans accents) quand la liste est longue ; le fan déjà choisi reste toujours dans la liste.
  const sansAccents = (t) => String(t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  // V5 point 8 : le serveur donne pour chaque vente ses candidats (fans qui ont reçu le lien AVANT la vente d'abord).
  const candidatsDe = (v) => (Array.isArray(v.candidats) && v.candidats.length
    ? v.candidats.filter((c, i, t) => c.conversation_id && t.findIndex((x) => x.conversation_id === c.conversation_id) === i)
    : candidats);
  const candidatsPour = (v) => {
    const base = candidatsDe(v);
    const q = sansAccents(rechercheFan[v.id]).trim();
    return q ? base.filter((c) => c.conversation_id === choixFan[v.id] || sansAccents(c.fan).includes(q)) : base;
  };
  const libelleCandidat = (c) => (c.lien_le
    ? `${c.fan || 'Fan'} · lien reçu le ${dateFr(c.lien_le)}, avant la vente`
    : `${c.fan || 'Fan'} · repris le ${dateFr(c.cree_le)}`);
  // V5 : classement du fan par le bot (reprises et « Lien envoyé »).
  const texteClasse = (classe, sousRaison) => (classe === 'pret' ? '🟢 prêt à payer'
    : classe === 'negocier' ? (sousRaison === 'sans_reponse' ? '🟡 pas répondu au lien' : '🟡 à négocier')
      : classe === 'time_waster' ? '🔴 time waster' : null);
  const nbZone = Number(reprise?.nb_zone || 0);

  // Le garde-fou ne vaut que pour le lien ENREGISTRÉ : tant qu'un changement n'est pas
  // enregistré, on n'affiche ni le vert ni le rouge (ils parleraient de l'ancien lien).
  const conflit = etat?.conflit;
  const modifie = (compte || '') !== (etat?.account_id || '') || lien.trim() !== (etat?.link_url || '');
  // E1 : actif = réglage enregistré (statut écrit par le serveur). E2 : modèle et lien réservés à manage_telegram.
  // 30/09 (Marlon) : « j'ai désactivé le bot d'accueil » — l'écran l'affichait éteint pendant que le
  // serveur le faisait tourner. Cet écran testait `statut === 'actif'` alors que le serveur considère
  // actifs `["actif", "actif_v3"]` (server.py, _BOT_STATUTS_ACTIFS). Avec `actif_v3` : écran éteint,
  // bot allumé. On lit donc TOUTE la famille « actif… », pour que le prochain `actif_v4` ne refasse
  // pas le même coup. Le serveur arrête le bot en écrivant un statut hors de cette famille.
  const estActif = (st) => /^actif/i.test(String(st || ''));
  const actif = estActif(etat?.statut) && !!etat?.account_id;
  const peutModifier = typeof etat?.peut_modifier === 'boolean' ? etat.peut_modifier : hasPerm('manage_telegram');
  const peutEnregistrer = modifie || (!actif && !!compte && !!lien.trim());   // réglage pas encore actif : un clic l'active
  const changeModele = actif && !!compte && compte !== etat.account_id;
  const nomModele = etat?.account_name || 'cette modèle';
  const modeleMessages = etat?.account_name || '[MODÈLE]';
  // E4 : dernière erreur de copie Dropp ou de réécriture Telegram, sur une ligne.
  const derniereErreur = etat?.derniere_erreur && typeof etat.derniere_erreur.quoi === 'string' ? etat.derniere_erreur : null;
  // #3/#11 : fichiers voisins de server.py pas à jour (pret=false, manque) ou alerte du serveur → une ligne rouge près du
  // statut, et plus de vert (« Actif » serait trompeur). Serveur sans ces champs : rien d'affiché.
  const pasPret = etat?.pret === false;
  const manque = Array.isArray(etat?.manque) ? etat.manque.filter((x) => typeof x === 'string' && x) : [];
  const alerte = typeof etat?.alerte === 'string' && etat.alerte.trim() ? etat.alerte.trim() : null;
  const actifOk = actif && !pasPret && !alerte;
  // Dernière modification : le nom de la personne ; un identifiant de compte (uuid) n'est jamais affiché.
  const modifiePar = typeof etat?.updated_by === 'string' && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(etat.updated_by.trim())
    ? etat.updated_by.trim() : '';
  // #23 : créneaux du planning illisibles (ex. « 11:00 » seul) : leurs chatteurs comptent hors shift.
  const creneauxIllisibles = Array.isArray(reprise?.creneaux_illisibles) ? reprise.creneaux_illisibles.filter((x) => typeof x === 'string' && x.trim()) : [];
  // Raison « personne » du serveur, sans « à attribuer » ; le créneau illisible qu'elle cite a déjà sa ligne ambre dessous.
  const raisonPersonne = (reprise?.personne || 'Personne en shift avec une box en ce moment')
    .replace(/\s*(?:—|:)\s*à attribuer$/, '')
    .replace(creneauxIllisibles.length ? /\s*\(créneaux? illisibles? :[^)]*\)/ : /(?!)/, '')
    .replace(/^./, (x) => x.toUpperCase());

  return (
    <div className="p-6 max-w-[1100px] mx-auto" data-testid="crm-bot-page">
      <h1 className="text-2xl font-bold text-white flex items-center gap-3">Bot d'accueil
        {configChargee && !erreurConfig && (actifOk ? (
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 flex items-center gap-1" data-testid="crm-bot-statut">
            <CheckCircle2 size={12} /> Actif sur {nomModele}
          </span>
        ) : actif ? (
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-500/15 text-red-300 border border-red-500/30 flex items-center gap-1" data-testid="crm-bot-statut">
            <AlertTriangle size={12} /> {pasPret ? 'Incomplet' : 'À vérifier'} sur {nomModele}
          </span>
        ) : (
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 text-slate-300 border border-white/10" data-testid="crm-bot-statut">Pas encore réglé</span>
        ))}
      </h1>
      {configChargee && !erreurConfig && (pasPret || alerte) && (
        <div className="mt-1 mb-1.5 flex items-start gap-2 min-w-0 text-[13px] text-red-300" data-testid="crm-bot-alerte">
          <AlertTriangle size={15} className="shrink-0 mt-0.5 text-red-400" />
          <span className="min-w-0 break-words">
            {pasPret && `Mise en ligne incomplète${manque.length ? ` : ${manque.join(', ')} pas à jour` : ''} — ChatHub ne suit pas vraiment le bot.`}
            {pasPret && alerte ? ' ' : null}
            {alerte}
          </span>
        </div>
      )}
      <p className="text-sm text-slate-400 mb-5">Le bot du CRM n°2 accueille les nouveaux fans jusqu'à son lien de paiement. ChatHub le suit et rend le fan aux chatteurs. Aide-mémoire plus bas.</p>

      {configChargee && !erreurConfig && !pasPret && (
        <div className={`rounded-2xl border p-4 mb-4 flex gap-3 text-[13px] ${actifOk ? 'border-emerald-500/25 bg-emerald-500/[0.05] text-emerald-100/90' : 'border-white/10 bg-white/[0.02] text-slate-300'}`} data-testid="crm-bot-bandeau">
          <ShieldCheck size={18} className={`shrink-0 mt-0.5 ${actifOk ? 'text-emerald-400' : 'text-slate-500'}`} />
          {actif ? (
            <div>
              <div className={`font-semibold mb-0.5 ${actifOk ? 'text-emerald-300' : 'text-slate-200'}`}>ChatHub suit le bot</div>
              Les nouveaux fans de {nomModele} restent cachés tant que le bot leur parle, puis vont au chatteur en shift.
            </div>
          ) : (
            <div>{peutModifier ? "Choisis la modèle et colle le lien du bot : ChatHub le suit dès l'enregistrement." : 'La modèle et le lien du bot sont réglés par le propriétaire.'}</div>
          )}
        </div>
      )}
      {derniereErreur && (
        <div className={`${pasPret ? '' : '-mt-2 '}mb-4 flex items-center gap-2 min-w-0 text-[12px] text-red-300`} data-testid="crm-bot-derniere-erreur" title={derniereErreur.quoi}>
          <AlertTriangle size={14} className="shrink-0 text-red-400" />
          <span className="truncate">Dernière erreur · {dateFr(derniereErreur.le)} : {derniereErreur.quoi}</span>
        </div>
      )}

      <Carte titre="Réglages" icon={Link2}>
        <div className="grid gap-4 md:grid-cols-[240px_1fr]">
          <div className="min-w-0">
            <label htmlFor="crm-bot-compte" className="text-[11px] text-slate-500 mb-1 block">Modèle sur laquelle tourne le bot</label>
            {peutModifier ? (
              <select id="crm-bot-compte" value={compte} onChange={(e) => setCompte(e.target.value)} data-testid="crm-bot-compte" disabled={!configChargee}
                className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-[13px] text-slate-200">
                <option value="">Choisir…</option>
                {comptes.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            ) : (
              // E2 : lecture seule pour un manager.
              <output id="crm-bot-compte" className="block rounded-lg border border-white/5 bg-black/20 px-3 py-2 text-[13px] text-slate-300 truncate" data-testid="crm-bot-compte-lecture">{etat?.account_name || '—'}</output>
            )}
          </div>
          <div className="min-w-0">
            <label htmlFor="crm-bot-lien" className="text-[11px] text-slate-500 mb-1 block">Lien Dropp envoyé par le bot (celui configuré dans le CRM n°2)</label>
            {peutModifier ? (
              <input id="crm-bot-lien" value={lien} onChange={(e) => setLien(e.target.value)} data-testid="crm-bot-lien" disabled={!configChargee}
                placeholder="https://app.dropp.fans/s/…"
                className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-[13px] text-slate-200 placeholder:text-slate-600" />
            ) : (
              <output id="crm-bot-lien" className="block rounded-lg border border-white/5 bg-black/20 px-3 py-2 text-[13px] text-slate-300 truncate select-all" data-testid="crm-bot-lien-lecture">{etat?.link_url || '—'}</output>
            )}
          </div>
        </div>

        {erreurConfig && (
          <div className="mt-4 rounded-xl border border-red-500/30 bg-red-500/[0.07] p-3 text-[13px] text-red-200">Impossible de charger les réglages. Recharge la page.</div>
        )}
        {peutModifier && modifie && configChargee && (
          <div className="mt-4 text-[12px] text-slate-500">Enregistre pour vérifier que ce lien est bien réservé au bot.</div>
        )}
        {peutModifier && changeModele && nbZone > 0 && (
          <div className="mt-2 text-[12px] text-amber-300" data-testid="crm-bot-changement-modele">
            {nbZone === 1 ? 'Le fan' : `Les ${nbZone} fans`} de la Zone Bot de {nomModele} {nbZone === 1 ? 'sera rendu' : 'seront rendus'} aux chatteurs.
          </div>
        )}
        {!modifie && conflit && conflit.envois > 0 && (
          <div className="mt-4 rounded-xl border border-red-500/30 bg-red-500/[0.07] p-3 flex gap-2.5 text-[13px] text-red-100/90" data-testid="crm-bot-conflit">
            <AlertTriangle size={17} className="text-red-400 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-red-300 mb-0.5">Ce lien est aussi envoyé par tes chatteurs</div>
              {conflit.envois} envoi{conflit.envois > 1 ? 's' : ''} à {conflit.fans} fan{conflit.fans > 1 ? 's' : ''} sur les 30 derniers jours. Toute vente sur ce lien part au bot, même celles de tes chatteurs. {peutModifier ? <><b className="text-red-200">Crée un lien réservé au bot</b> dans Dropp et colle-le ici.</> : 'Préviens le propriétaire.'}
            </div>
          </div>
        )}
        {!modifie && conflit && conflit.envois === 0 && (
          <div className="mt-4 rounded-xl border border-emerald-500/25 bg-emerald-500/[0.05] p-3 flex gap-2.5 text-[13px] text-emerald-100/90" data-testid="crm-bot-sans-conflit">
            <CheckCircle2 size={17} className="text-emerald-400 shrink-0 mt-0.5" />
            <div>Aucun chatteur n'a envoyé ce lien ces 30 derniers jours : il est bien réservé au bot.</div>
          </div>
        )}

        <div className="mt-4 flex items-center justify-between gap-3">
          <div className="text-[11px] text-slate-500">
            {etat?.updated_at ? <>Dernière modification : {dateFr(etat.updated_at)}{modifiePar ? ` · ${modifiePar}` : ''}</> : 'Pas encore réglé.'}
            {configChargee && !peutModifier && etat?.updated_at && ' · modifiable par le propriétaire'}
          </div>
          {peutModifier && (
            <button onClick={enregistrer} disabled={enreg || !peutEnregistrer || !configChargee} data-testid="crm-bot-enregistrer"
              className="rounded-lg px-4 py-2 text-[13px] font-semibold bg-indigo-500 text-white hover:bg-indigo-400 disabled:opacity-40 disabled:cursor-not-allowed">
              {enreg ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          )}
        </div>
      </Carte>

      <Carte titre="Ce qu'a fait le bot — 30 derniers jours" icon={Bot}
        droite={<button onClick={chargerApercu} className="text-slate-400 hover:text-white" title="Actualiser" aria-label="Actualiser"><RefreshCw size={15} className={chargeApercu ? 'animate-spin' : ''} /></button>}>
        {erreurApercu && (
          <div className="mb-3 rounded-xl border border-red-500/30 bg-red-500/[0.07] p-3 text-[13px] text-red-200" data-testid="crm-bot-erreur-apercu">Impossible de charger l'aperçu. Réessaie avec le bouton d'actualisation.</div>
        )}
        {!apercu ? (
          !erreurApercu && <div className="text-[13px] text-slate-500">Chargement…</div>
        ) : !apercu.configure ? (
          <div className="text-[13px] text-slate-500">Règle d'abord la modèle et le lien du bot : l'aperçu apparaîtra ici.</div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="rounded-xl border border-white/10 bg-black/20 p-3">
                <div className="text-[11px] text-slate-500">Liens envoyés par le bot</div>
                <div className="text-xl font-bold text-white" data-testid="crm-bot-liens">{apercu.liens_envoyes}</div>
              </div>
              <div className="rounded-xl border border-white/10 bg-black/20 p-3">
                <div className="text-[11px] text-slate-500">Fans touchés</div>
                <div className="text-xl font-bold text-white">{apercu.nb_fans}</div>
              </div>
              <div className="rounded-xl border border-violet-500/25 bg-violet-500/[0.06] p-3">
                <div className="text-[11px] text-violet-300/80">Ventes sur le lien du bot</div>
                <div className="text-xl font-bold text-violet-200">{apercu.ventes} · {Number(apercu.total_eur || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</div>
              </div>
            </div>
            {apercu.nb_fans === 0 ? (
              <div className="text-[13px] text-slate-500">Le bot n'a encore envoyé aucun lien sur ce compte. Dès qu'il le fera, les fans apparaîtront ici.</div>
            ) : (
              <div className="rounded-xl border border-white/10 overflow-hidden">
                <div className="grid grid-cols-[1fr_130px_100px_110px] gap-2 px-3 py-2 text-[11px] text-slate-500 border-b border-white/10">
                  <span>Fan</span><span>Lien envoyé le</span><span className="text-right">A dépensé</span><span className="text-right">Payé sur le lien</span>
                </div>
                {apercu.fans.map((f) => (
                  <button type="button" key={f.conversation_id}
                    onClick={() => navigate(`/dashboard/messages?account=${apercu.account_id}&conv=${f.conversation_id}`)}
                    className="w-full text-left grid grid-cols-[1fr_130px_100px_110px] gap-2 px-3 py-2 text-[13px] text-slate-200 border-b border-white/5 last:border-0 hover:bg-white/[0.03]">
                    <span className="truncate">{f.nom}</span>
                    <span className="text-slate-400">{dateFr(f.envoye_le)}</span>
                    <span className="text-right">{Number(f.a_depense || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span>
                    <span className="text-right text-violet-200" data-testid={`crm-bot-paye-bot-${f.conversation_id}`}>{Number(f.paye_bot || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </Carte>

      <Carte titre="Zone Bot et reprise des fans" icon={Repeat} testid="crm-bot-reprise"
        droite={reprise?.configure && (
          <div className="flex items-center gap-2.5">
            <span className="text-[12px] text-slate-400" data-testid="crm-bot-nb-zone">{nbZone} fan{nbZone > 1 ? 's' : ''} en Zone Bot</span>
            <button type="button" onClick={() => navigate(`/dashboard/messages?account=${reprise.account_id}&zone=bot`)} data-testid="crm-bot-ouvrir-zone"
              className="text-[12px] px-2.5 py-1 rounded-md border border-white/10 text-slate-300 hover:text-white hover:bg-white/5 flex items-center gap-1.5">
              <EyeOff size={13} /> Ouvrir la Zone Bot
            </button>
          </div>
        )}>
        {repriseKo && (
          <div className="mb-3 rounded-xl border border-red-500/30 bg-red-500/[0.07] p-3 text-[13px] text-red-200">Impossible de charger la reprise des fans. Recharge la page.</div>
        )}
        {!reprise ? (
          !repriseKo && <div className="text-[13px] text-slate-500">Chargement…</div>
        ) : !reprise.configure ? (
          <div className="text-[13px] text-slate-500">Règle d'abord la modèle et le lien du bot.</div>
        ) : (
          <>
            <div className="text-[12px] text-slate-400 mb-3">
              Nouveau fan (moins de 3 jours) caché aux chatteurs jusqu'au lien du bot. Après le lien, le bot le classe : 🟢 prêt à payer et 🟡 à négocier reviennent aux chatteurs (notification orange pour les 🟢), 🔴 time waster reste caché. Sans lien au bout de 3 jours : rendu sans notification.
              <span className="block mt-1.5 text-slate-300" data-testid="crm-bot-repartition">Répartition automatique par le planning : le fan va au chatteur <b className="text-slate-200">en shift</b> sur {reprise.compte || 'ce compte'} (chacun son tour), sinon à celui du prochain shift. La box est facultative : sans box réglée, le fan garde la sienne.</span>
            </div>

            {(reprise.chatteurs || []).length === 0 ? (
              <div className="text-[13px] text-slate-500">Aucun chatteur n'a accès à {reprise.compte || 'ce compte'}.</div>
            ) : (
              <div className="rounded-xl border border-white/10 overflow-hidden">
                <div className="grid grid-cols-[1fr_100px_120px] gap-2 px-3 py-2 text-[11px] text-slate-500 border-b border-white/10">
                  <span>Chatteur</span><span>Shift</span><span>Box (facultatif)</span>
                </div>
                {reprise.chatteurs.map((c) => (
                  <div key={c.id} className="grid grid-cols-[1fr_100px_120px] gap-2 px-3 py-2 text-[13px] text-slate-200 border-b border-white/5 last:border-0 items-center" data-testid={`crm-bot-chatteur-${c.id}`}>
                    <span className="min-w-0">
                      <span className="block truncate">{c.name || '—'}</span>
                      {/* Sans connexion, il ne reçoit ni fan ni notification : pas de box possible. */}
                      {c.connexion === false && <span className="block text-[11px] text-amber-300" data-testid={`crm-bot-sans-connexion-${c.id}`}>pas de connexion</span>}
                      {c.box_invalide ? <span className="block text-[11px] text-amber-300" data-testid={`crm-bot-box-invalide-${c.id}`}>⚠ Box {c.box_invalide} n'existe plus</span> : null}
                    </span>
                    <span>
                      {c.en_shift
                        ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">en shift</span>
                        : <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 text-slate-400 border border-white/10">hors shift</span>}
                    </span>
                    <select value={boxes[c.id] || ''} aria-label={`Box de ${c.name || 'ce chatteur'}`} data-testid={`crm-bot-box-${c.id}`}
                      disabled={c.connexion === false} title={c.connexion === false ? 'Pas de connexion' : undefined}
                      onChange={(e) => { const v = e.target.value; setBoxes((p) => ({ ...p, [c.id]: v ? Number(v) : null })); }}
                      className="w-full rounded-lg border border-white/10 bg-black/30 px-2 py-1 text-[13px] text-slate-200 disabled:opacity-40 disabled:cursor-not-allowed">
                      <option value="">—</option>
                      {Array.from({ length: Number(reprise.nb_boxes || 0) }, (_, i) => i + 1).map((n) => <option key={n} value={n}>Box {n}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-3 text-[13px] text-slate-300" data-testid="crm-bot-prochain">
              {reprise.prochain
                ? <>Prochain fan → <b className="text-white">{reprise.prochain.name}</b> (Box {reprise.prochain.box})</>
                : reprise.prochain_shift
                  ? <span data-testid="crm-bot-prochain-shift">Hors shift → <b className="text-white">{reprise.prochain_shift.name}</b> (Box {reprise.prochain_shift.box}), pour son prochain shift{reprise.prochain_shift.quand ? ` (${reprise.prochain_shift.quand})` : ''}</span>
                  : <span className="text-amber-300">{raisonPersonne}</span>}
              {modifieReprise && <span className="text-[11px] text-slate-500"> · selon le réglage enregistré</span>}
            </div>
            {creneauxIllisibles.length > 0 && (
              <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-amber-300" data-testid="crm-bot-creneaux-illisibles">
                <span>{creneauxIllisibles.length > 1 ? 'Créneaux illisibles' : 'Créneau illisible'} : {creneauxIllisibles.map((x) => `« ${x.trim()} »`).join(', ')} — {creneauxIllisibles.length > 1 ? 'leurs' : 'ses'} chatteurs comptent hors shift.</span>
                {hasPerm('planning_manage') && (
                  <button type="button" onClick={() => navigate('/dashboard/employees/planning/manage')} data-testid="crm-bot-gerer-planning"
                    className="underline underline-offset-2 hover:text-amber-200">Gérer le planning</button>
                )}
              </div>
            )}

            <div className="mt-4 flex items-center justify-end gap-3">
              {erreurReprise && <div className="mr-auto text-[12px] text-red-300" data-testid="crm-bot-reprise-erreur">{erreurReprise}</div>}
              <button onClick={enregistrerReprise} disabled={enregReprise || !modifieReprise} data-testid="crm-bot-reprise-enregistrer"
                className="rounded-lg px-4 py-2 text-[13px] font-semibold bg-indigo-500 text-white hover:bg-indigo-400 disabled:opacity-40 disabled:cursor-not-allowed">
                {enregReprise ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>

            {(reprise.reprises || []).length > 0 && (
              <div className="mt-4" data-testid="crm-bot-reprises">
                <div className="text-[11px] text-slate-500 mb-1.5">Dernières reprises</div>
                <div className="rounded-xl border border-white/10 overflow-hidden">
                  {reprise.reprises.map((r) => (
                    <button type="button" key={r.conversation_id}
                      onClick={() => navigate(`/dashboard/messages?account=${reprise.account_id}&conv=${r.conversation_id}`)}
                      className="w-full text-left flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-[13px] text-slate-200 border-b border-white/5 last:border-0 hover:bg-white/[0.03]">
                      <span className="truncate max-w-[200px]">{r.nom}</span>
                      <span className="text-[12px] text-slate-400">{dateFr(r.le)}</span>
                      {r.bloque
                        ? <span className="text-[12px] text-slate-400" data-testid={`crm-bot-reprise-bloque-${r.conversation_id}`}>bloqué</span>
                        : r.chatter_name
                          ? <span className="text-[12px] text-slate-300">→ {r.chatter_name}{r.box ? ` · Box ${r.box}` : ''}</span>
                          : r.raison === 'plus_nouveau'
                            ? <span className="text-[12px] text-slate-400">dans sa box</span>
                            : <span className="text-[12px] text-amber-300">sans chatteur</span>}
                      <span className="ml-auto flex items-center gap-1.5">
                        {r.raison === 'lien_envoye' && (r.lien_remplace
                          ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">lien remplacé</span>
                          : <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30">lien d'origine</span>)}
                        {r.raison === 'lien_envoye' && r.raison_avant && <span data-testid={`crm-bot-reprise-apres-sortie-${r.conversation_id}`} className="text-[11px] px-2 py-0.5 rounded-full bg-sky-500/15 text-sky-300 border border-sky-500/30">lien reçu après sa sortie</span>}
                        {r.raison === 'manuel' && <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 text-slate-400 border border-white/10">rendu à la main</span>}
                        {texteClasse(r.classe, r.sous_raison) && <span data-testid={`crm-bot-reprise-classe-${r.conversation_id}`} className={`text-[11px] px-2 py-0.5 rounded-full border ${r.classe === 'pret' ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' : r.classe === 'negocier' ? 'bg-amber-500/15 text-amber-300 border-amber-500/30' : 'bg-rose-500/15 text-rose-300 border-rose-500/30'}`}>{texteClasse(r.classe, r.sous_raison)}</span>}
                        {r.raison === 'plus_nouveau' && <span data-testid={`crm-bot-reprise-plus-nouveau-${r.conversation_id}`} className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 text-slate-400 border border-white/10">plus nouveau (3 jours), sans lien</span>}
                        {r.raison === 'silence_bot' && <span className="text-[11px] px-2 py-0.5 rounded-full bg-orange-500/15 text-orange-300 border border-orange-500/30">sans lien (ancienne règle des 2 h)</span>}
                        {r.raison === 'delai' && <span className="text-[11px] px-2 py-0.5 rounded-full bg-orange-500/15 text-orange-300 border border-orange-500/30">12 h sans lien</span>}
                        {r.attend_shift && <span className="text-[11px] px-2 py-0.5 rounded-full bg-sky-500/15 text-sky-300 border border-sky-500/30">attend son shift</span>}
                        {r.raison === 'bot_arrete' && <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 text-slate-400 border border-white/10">bot arrêté</span>}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </Carte>

      {timeWasters && (
        <Carte titre="Time wasters" icon={Ban} testid="crm-bot-time-wasters"
          droite={<span className="text-[12px] text-slate-400" data-testid="crm-bot-tw-nb">{timeWasters.length} 🔴</span>}>
          <div className="text-[12px] text-slate-400 mb-3">Cachés dans toute la messagerie. Rends-en un s&apos;il mérite une chance.</div>
          {!timeWasters.length ? <div className="text-[13px] text-slate-400" data-testid="crm-bot-tw-vide">Aucun time waster.</div> : (
            <div className="rounded-xl border border-white/10 overflow-hidden">
              {timeWasters.map((f) => (
                <div key={f.conversation_id} data-testid={`crm-bot-tw-${f.conversation_id}`} className="border-b border-white/5 last:border-0">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-[13px] text-slate-200">
                    <span className="font-semibold truncate max-w-[180px]">{f.nom}</span>
                    <span className="text-[12px] text-slate-400">lien le {dateFr(f.lien_le || f.classe_le)}</span>
                    <span className="text-[12px] text-slate-300" data-testid={`crm-bot-tw-depense-${f.conversation_id}`}>dépensé {Number(f.spent || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span>
                    {f.bloque && <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 text-slate-400 border border-white/10">bloqué</span>}
                    <span className="basis-full text-[12px] text-rose-200/90" data-testid={`crm-bot-tw-raison-${f.conversation_id}`}>🔴 {f.raison || 'time waster'}</span>
                    <span className="basis-full flex flex-wrap items-center justify-end gap-2">
                      <button type="button" onClick={() => ouvrirTimeWaster(f)} data-testid={`crm-bot-tw-ouvrir-${f.conversation_id}`}
                        className="text-[12px] px-2.5 py-1 rounded-md border border-white/10 text-slate-300 hover:text-white hover:bg-white/5">
                        {twFil.id === f.conversation_id ? 'Fermer' : 'Ouvrir la conversation'}
                      </button>
                      <button type="button" onClick={() => rendreTimeWaster(f)} disabled={twEnCours === f.conversation_id} data-testid={`crm-bot-tw-rendre-${f.conversation_id}`}
                        className="text-[12px] px-2.5 py-1 rounded-md border border-violet-400/40 bg-violet-500/15 text-violet-100 hover:bg-violet-500/25 disabled:opacity-40">
                        {twEnCours === f.conversation_id ? '…' : 'Rendre aux chatteurs'}
                      </button>
                    </span>
                  </div>
                  {twFil.id === f.conversation_id && (
                    <div className="mx-3 mb-3 max-h-72 overflow-y-auto rounded-lg border border-white/10 bg-black/30 p-2 space-y-1" data-testid={`crm-bot-tw-fil-${f.conversation_id}`}>
                      {twFil.charge ? <div className="text-[12px] text-slate-400">Chargement…</div>
                        : twFil.erreur ? <div className="text-[12px] text-red-300">{twFil.erreur}</div>
                          : !twFil.messages.length ? <div className="text-[12px] text-slate-400">Aucun message.</div>
                            : twFil.messages.map((m, i) => (
                              <div key={m.id || i} className={`flex ${m.from_ === 'them' ? 'justify-start' : 'justify-end'}`}>
                                <span className={`max-w-[80%] rounded-lg px-2 py-1 text-[12px] whitespace-pre-wrap break-words ${m.from_ === 'them' ? 'bg-white/10 text-slate-100' : 'bg-violet-500/20 text-violet-100'}`}>{m.text || '(média)'}</span>
                              </div>
                            ))}
                      <div className="text-[11px] text-slate-500 pt-1">Lecture seule. Pour lui répondre : « Rendre aux chatteurs ».</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Carte>
      )}

      {aIdentifier.ventes.length > 0 && (
        <Carte titre="Ventes du bot à identifier" icon={HelpCircle} testid="crm-bot-a-identifier">
          <div className="text-[12px] text-slate-400 mb-3">La vente reste créditée au Bot : ça sert seulement à savoir quel fan a payé.</div>
          <div className="grid gap-2">
            {aIdentifier.ventes.map((v) => { const tous = candidatsDe(v); const liste = candidatsPour(v); return (
              <div key={v.id} className="rounded-xl border border-violet-500/25 bg-violet-500/[0.06] p-3 flex flex-wrap items-center gap-x-3 gap-y-2">
                <span className="text-[13px] font-bold text-violet-200">{Number(v.amount || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span>
                <span className="text-[12px] text-slate-400">{dateFr(v.date)}</span>
                {typeof v.client === 'string' && v.client && <span className="text-[12px] text-slate-300 truncate max-w-[180px]">{v.client}</span>}
                <div className="ml-auto flex flex-wrap items-center gap-2">
                  {tous.length > 10 && (
                    <input value={rechercheFan[v.id] || ''} onChange={(e) => { const t = e.target.value; setRechercheFan((p) => ({ ...p, [v.id]: t })); }}
                      placeholder="Chercher un fan…" aria-label="Chercher un fan" data-testid={`crm-bot-recherche-${v.id}`}
                      className="rounded-lg border border-white/10 bg-black/30 px-2 py-1 text-[13px] text-slate-200 placeholder:text-slate-600 w-[150px]" />
                  )}
                  <select value={choixFan[v.id] || ''} onChange={(e) => { const cid = e.target.value; setChoixFan((p) => ({ ...p, [v.id]: cid })); }}
                    disabled={tous.length === 0} aria-label="Fan qui a payé" data-testid={`crm-bot-candidat-${v.id}`}
                    className="rounded-lg border border-white/10 bg-black/30 px-2 py-1 text-[13px] text-slate-200 max-w-[320px]">
                    <option value="">{!tous.length ? 'Aucun fan candidat' : liste.length ? 'Choisir le fan…' : 'Aucun fan trouvé'}</option>
                    {liste.map((c) => <option key={c.conversation_id} value={c.conversation_id}>{libelleCandidat(c)}</option>)}
                  </select>
                  <button type="button" onClick={() => identifierVente(v)} disabled={!choixFan[v.id] || identifEnCours === v.id} data-testid={`crm-bot-identifier-${v.id}`}
                    className="text-[12px] px-2.5 py-1 rounded-md border border-white/10 text-slate-200 hover:text-white hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed">
                    {identifEnCours === v.id ? '…' : "C'est lui"}
                  </button>
                </div>
              </div>
            ); })}
          </div>
        </Carte>
      )}

      {/* ───────────────────────── AIDE-MÉMOIRE ───────────────────────── */}
      <div className="mt-8 mb-4 flex items-center gap-3" data-testid="crm-bot-aide-memoire">
        <BookOpen size={18} className="text-indigo-300" />
        <div>
          <div className="text-lg font-bold text-white">Aide-mémoire — comment ça marche</div>
          <div className="text-[12px] text-slate-500">Ce qu'on a décidé ensemble.</div>
        </div>
      </div>

      <Carte titre="1. Le parcours d'un nouveau fan" icon={MessageSquareShare}>
        <ol className="grid gap-2.5 md:grid-cols-2">
          {ETAPES.map((e, i) => (
            <li key={e.titre} className="rounded-xl border border-white/10 bg-black/20 p-3 flex gap-3">
              <div className="w-7 h-7 rounded-lg bg-white/5 text-slate-300 flex items-center justify-center text-[12px] font-bold shrink-0">{i + 1}</div>
              <div>
                <div className="text-[13px] font-semibold text-white flex items-center gap-1.5"><e.icon size={14} className="text-slate-400" />{e.titre}</div>
                <div className="text-[12px] text-slate-400 mt-0.5">{e.texte}</div>
              </div>
            </li>
          ))}
        </ol>
      </Carte>

      <Carte titre="2. Les notifications" icon={Flame}
        droite={(
          <button type="button" onClick={voirNotifs} data-testid="crm-bot-voir-notifs"
            className="text-[12px] px-2.5 py-1 rounded-md border border-white/10 text-slate-300 hover:text-white hover:bg-white/5 flex items-center gap-1.5">
            <Eye size={13} /> Voir en vrai
          </button>
        )}>
        <div className="grid gap-2.5">
          {NOTIFS.map((n) => (
            <div key={n.titre} className={`rounded-xl border p-3 ${TEINTE[n.couleur]}`}>
              <div className="flex items-start gap-2.5">
                <n.icon size={17} className="shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-bold">{n.titre}</div>
                  <div className="text-[12px] text-slate-300">{n.texte}</div>
                  <div className="text-[11px] text-slate-500 mt-1">{n.quand}</div>
                </div>
                <span className="text-[11px] px-2 py-1 rounded-md border border-white/10 text-slate-300 shrink-0">Ouvrir le chat</span>
              </div>
            </div>
          ))}
        </div>
        <div className="text-[11px] text-slate-500 mt-3">Pas de notification « n'a pas payé » : décidé le 12/09 pour garder peu de couleurs. L'orange suffit — le chatteur négocie jusqu'au paiement.</div>
      </Carte>

      <Carte titre="3. Comment s'affichent les ventes du bot" icon={Coins} testid="crm-bot-affichage-ventes">
        <div className="text-[12px] text-slate-400 mb-3">Dans la liste des ventes, la colonne « Vendu par » affiche un badge <span className="text-violet-300 font-semibold">violet « Bot »</span> au lieu du nom d'un chatteur. D'un coup d'œil tu vois ce qui vient du bot et ce qui vient de l'équipe. <span className="text-slate-500">(Exemple fictif.)</span></div>
        <div className="rounded-xl border border-white/10 overflow-hidden">
          <div className="grid grid-cols-[60px_1fr_110px_90px] md:grid-cols-[60px_110px_110px_90px_1fr] gap-2 px-3 py-2 text-[11px] text-slate-500 border-b border-white/10">
            <span>Heure</span><span>Fan</span><span>Vendu par</span><span className="text-right">Montant</span><span className="hidden md:block">Pourquoi</span>
          </div>
          {VENTES_EXEMPLE.map((v) => (
            <div key={v.heure} className="grid grid-cols-[60px_1fr_110px_90px] md:grid-cols-[60px_110px_110px_90px_1fr] gap-2 px-3 py-2.5 text-[13px] text-slate-200 border-b border-white/5 last:border-0 items-center">
              <span className="text-slate-400">{v.heure}</span>
              <span className="truncate">{v.fan}</span>
              <span>
                {v.par === 'bot'
                  ? <span className="text-[11px] px-2 py-0.5 rounded-md bg-violet-500/15 text-violet-300 border border-violet-500/30 inline-flex items-center gap-1"><Bot size={12} /> Bot</span>
                  : <span className="text-[11px] px-2 py-0.5 rounded-md bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">{v.par}</span>}
              </span>
              <span className="text-right font-semibold">{v.montant}</span>
              <span className="hidden md:block text-[12px] text-slate-500">{v.note}</span>
            </div>
          ))}
        </div>
      </Carte>

      <div className="grid gap-4 md:grid-cols-2">
        <Carte titre="4. Qui touche quoi" icon={Users}>
          <div className="grid gap-2">
            <div className="rounded-xl border border-violet-500/25 bg-violet-500/[0.06] p-3">
              <div className="text-[13px] font-semibold text-violet-200 flex items-center gap-1.5"><Bot size={14} /> La première vente</div>
              <div className="text-[12px] text-slate-400 mt-0.5">Créditée au <b className="text-violet-200">Bot</b>, jamais à un chatteur. Elle ne compte pas dans les chiffres de l'équipe.</div>
            </div>
            <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/[0.06] p-3">
              <div className="text-[13px] font-semibold text-emerald-200 flex items-center gap-1.5"><Wallet size={14} /> Toutes les suivantes</div>
              <div className="text-[12px] text-slate-400 mt-0.5">Au chatteur qui a repris le fan, comme aujourd'hui.</div>
            </div>
            <div className="text-[11px] text-slate-500">Le lien du bot doit lui être réservé : s'il part chez un chatteur, ses ventes seraient créditées au bot. Le garde-fou des Réglages le signale.</div>
          </div>
        </Carte>

        <Carte titre="5. Le cas rare" icon={HelpCircle}>
          <div className="text-[13px] text-slate-300 leading-relaxed">
            Un fan qui clique le lien d'origine <b className="text-white">dans les 2-3 secondes</b> avant qu'il soit remplacé paie sans être reconnu.
          </div>
          <div className="mt-3 rounded-xl border border-dashed border-white/15 bg-black/20 p-3">
            <div className="text-[13px] font-semibold text-white flex items-center gap-1.5"><Bot size={14} className="text-slate-400" /> Vente du bot — 20,00 € <span className="font-normal text-amber-300">· fan à identifier</span></div>
            <div className="text-[12px] text-slate-500 mt-0.5">Le manager la rattache au bon fan dans « Ventes du bot à identifier », plus haut sur cette page.</div>
          </div>
          <div className="text-[11px] text-slate-500 mt-3">Ce clic <b className="text-slate-300">ne crédite pas</b> la vente au chatteur : elle reste au bot. Volume attendu : environ 1 première vente par jour chez Sara, dont une petite fraction dans ce cas.</div>
        </Carte>
      </div>

      <Carte titre="6. Ce que doit faire le chatteur qui reprend un fan" icon={ListChecks}>
        <div className="grid gap-3 md:grid-cols-2">
          <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/[0.05] p-3">
            <div className="text-[12px] font-semibold text-emerald-300 mb-2 flex items-center gap-1.5"><CheckCircle2 size={14} /> À faire</div>
            <ul className="grid gap-1.5">
              {A_FAIRE.map((t) => <li key={t} className="text-[13px] text-slate-300 flex gap-2"><span className="text-emerald-400">•</span>{t}</li>)}
            </ul>
          </div>
          <div className="rounded-xl border border-red-500/25 bg-red-500/[0.05] p-3">
            <div className="text-[12px] font-semibold text-red-300 mb-2 flex items-center gap-1.5"><Ban size={14} /> À ne pas faire</div>
            <ul className="grid gap-1.5">
              {A_NE_PAS_FAIRE.map((t) => <li key={t} className="text-[13px] text-slate-300 flex gap-2"><span className="text-red-400">•</span>{t}</li>)}
            </ul>
          </div>
        </div>
      </Carte>

      <Carte titre="7. Messages pour l'équipe" icon={MessageSquareShare} testid="crm-bot-messages">
        <div className="text-[12px] text-slate-400 mb-3">Remplace <b className="text-slate-200">[DATE]</b> avant d'envoyer. Clique sur un titre pour lire le message, « Copier » pour le coller dans le groupe.</div>
        <div className="grid gap-2.5">
          <MessageACopier titre="Message aux chatteurs" pour="groupe des chatteurs" texte={messageChatteurs(modeleMessages)} testid="crm-bot-message-chatteurs" />
          <MessageACopier titre="Message au manager" pour="manager" texte={messageManager(modeleMessages)} testid="crm-bot-message-manager" />
        </div>
      </Carte>
    </div>
  );
}
